import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    PointerSensor,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import moment from 'moment';

import { Spinner } from '../Spinner';
import { FaIcon } from '../FaIcon';
import { PortalReportsApi } from '../../api/portalReports';
import { buildLoadViewUrl } from '../../utils/savedViews';
import { explorerPath } from '../../hooks/useReportModality';
import { formatFieldId, metricItemToFieldId } from '../../utils/fieldLabels';
import { getPropertiesForPeriod } from '../../periods';
import { usePortalSubscriptionGating } from '../../utils/usePortalSubscriptionGating';
import { DuplicateViewModal } from './DuplicateViewModal';
import { EditViewMetadataModal } from '../data_explorer/SavedViews/EditViewMetadataModal';
import { YesNoModal } from '../elements/YesNoModal';

const PORTAL_SOFT_CAP = 4;
const CHIPS_VISIBLE_LIMIT = 4;

function formatViewPeriod(period, t) {
    if (!period) return '';
    if (period.type === 'FIXED' && period.start_date && period.end_date) {
        const from = moment(period.start_date, 'YYYY-MM-DD').format('DD MMM YYYY');
        const to = moment(period.end_date, 'YYYY-MM-DD').format('DD MMM YYYY');
        return `${from} → ${to}`;
    }
    if (period.type === 'RELATIVE' && period.predefined_period) {
        const props = getPropertiesForPeriod(t)[period.predefined_period];
        return props ? props.name : period.predefined_period;
    }
    return '';
}

function FieldChipsLine({ label, fields, t }) {
    if (!fields || fields.length === 0) return null;
    // Dedup defensivo: si dos field IDs resuelven al mismo display label,
    // mostrar solo uno (mantener el primero).
    const dedupedFields = Array.from(new Set(fields));
    const visible = dedupedFields.slice(0, CHIPS_VISIBLE_LIMIT);
    const overflow = dedupedFields.slice(CHIPS_VISIBLE_LIMIT);
    const overflowList = overflow.map((f) => `• ${f}`).join('<br/>');

    return (
        <div className="portal-reports-row-fields-line">
            <span className="portal-reports-row-fields-label">{label}</span>
            {visible.map((f, i) => (
                <span key={`${f}-${i}`} className="portal-reports-row-field-chip">{f}</span>
            ))}
            {overflow.length > 0 && (
                <span className="info-icon-container portal-reports-row-field-overflow">
                    {t('portal_reports.fields_overflow', { count: overflow.length })}
                    <div className="warning-tooltip -below -rich">
                        <div className="warning-tooltip-body">
                            <p className="warning-tooltip-text"
                               dangerouslySetInnerHTML={{ __html: overflowList }} />
                        </div>
                    </div>
                </span>
            )}
        </div>
    );
}

function ViewRow({ view, isDraggable, t, onAction, canAssignToPortal }) {
    const sortable = useSortable({ id: view.id, disabled: !isDraggable });
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [menuOpen]);

    const style = isDraggable
        ? {
            transform: CSS.Transform.toString(transform),
            transition: isDragging ? 'none' : transition,
            opacity: isDragging ? 0.6 : 1,
        }
        : {};

    const handleAction = (action) => {
        setMenuOpen(false);
        onAction(action, view);
    };

    return (
        <div ref={setNodeRef} style={style} className="portal-reports-row">
            {isDraggable && (
                <span
                    className="portal-reports-row-handle"
                    {...attributes}
                    {...listeners}
                    title={t('portal_reports.drag_handle')}
                >
                    <FaIcon iconName="grip-vertical" />
                </span>
            )}

            {view.assigned_to_portal && (
                <span className="portal-reports-row-order">
                    #{(view.portal_order ?? 0) + 1}
                </span>
            )}

            <div className="portal-reports-row-main">
                <div className="portal-reports-row-name-row">
                    <span className="portal-reports-row-name">{view.name}</span>
                    {view.assigned_to_portal && (
                        <span className="saved-view-portal-badge">
                            {t('saved_views.badge_in_portal')}
                        </span>
                    )}
                </div>
                {view.description && (
                    <div className="portal-reports-row-desc">{view.description}</div>
                )}
                <div className="portal-reports-row-fields">
                    <FieldChipsLine
                        label={t('portal_reports.fields_metrics_label')}
                        fields={(view.config?.metrics || [])
                            .map((m) => formatFieldId(metricItemToFieldId(m)))
                            .filter(Boolean)}
                        t={t}
                    />
                    <FieldChipsLine
                        label={t('portal_reports.fields_dimensions_label')}
                        fields={(view.config?.dimensions || [])
                            .map((d) => formatFieldId(d))
                            .filter(Boolean)}
                        t={t}
                    />
                    <FieldChipsLine
                        label={t('portal_reports.fields_period_label')}
                        fields={[formatViewPeriod(view.config?.period, t)].filter(Boolean)}
                        t={t}
                    />
                </div>
            </div>

            <div className="portal-reports-row-actions" ref={menuRef}>
                {view.assigned_to_portal ? (
                    <button
                        type="button"
                        className="portal-reports-row-unassign-btn"
                        onClick={() => handleAction('unassign')}
                        title={t('portal_reports.action_unassign')}
                    >
                        <FaIcon iconName="xmark" />
                        {t('portal_reports.action_unassign_short')}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="portal-reports-row-assign-btn"
                        onClick={() => handleAction('assign')}
                        disabled={!canAssignToPortal}
                        title={canAssignToPortal
                            ? t('portal_reports.action_assign')
                            : t('saved_views.upgrade_hint')}
                    >
                        <FaIcon iconName="bookmark" />
                        {t('portal_reports.action_assign_short')}
                    </button>
                )}
                <button
                    type="button"
                    className="portal-reports-row-kebab"
                    onClick={() => setMenuOpen((o) => !o)}
                    title={t('portal_reports.more_actions')}
                >
                    <FaIcon iconName="ellipsis-v" />
                </button>
                {menuOpen && (
                    <div className="portal-reports-row-menu">
                        <button onClick={() => handleAction('open')}>
                            {t('portal_reports.action_open_in_explorer')}
                        </button>
                        <button onClick={() => handleAction('rename')}>
                            {t('saved_views.action_rename_edit')}
                        </button>
                        <button onClick={() => handleAction('duplicate')}>
                            {t('portal_reports.action_duplicate_to_others')}
                        </button>
                        <button
                            onClick={() => handleAction('delete')}
                            className="portal-reports-row-menu-danger"
                        >
                            {t('portal_reports.action_delete')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Sección embebible que muestra la gestión de vistas del Explorador asignadas
 * al portal del cliente. Se monta dentro de `ClientPortalTab` cuando la agency
 * está en modalidad `DATA_EXPLORER`. Sin layout propio: solo el contenido.
 */
export function ClientPortalReportsSection({ clientId, onCountChange, onAssigned }) {
    const { t } = useTranslation();
    const { canAssignToPortal, loading: gatingLoading } = usePortalSubscriptionGating();

    const [loading, setLoading] = useState(true);
    const [views, setViews] = useState([]);
    const [duplicateView, setDuplicateView] = useState(null);
    const [renameView, setRenameView] = useState(null);
    const [deleteView, setDeleteView] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const loadingTimeoutRef = useRef(null);

    const fetchViews = useCallback(() => {
        if (!clientId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = setTimeout(() => {
            setLoading(false);
            toast.error(t('portal_reports.load_error'));
        }, 15000);

        PortalReportsApi.listViews(clientId)
            .then((response) => {
                if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
                if (!response || response.status === 'ERROR') {
                    toast.error((response && response.error_message) || t('portal_reports.load_error'));
                    setLoading(false);
                    return;
                }
                setViews(Array.isArray(response.views) ? response.views : []);
                setLoading(false);
            })
            .catch(() => {
                if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
                toast.error(t('portal_reports.load_error'));
                setLoading(false);
            });
    }, [clientId, t]);

    useEffect(() => {
        fetchViews();
        return () => {
            if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
        };
    }, [fetchViews]);

    const assigned = useMemo(
        () => views
            .filter((v) => v.assigned_to_portal)
            .sort((a, b) => (a.portal_order ?? 0) - (b.portal_order ?? 0)),
        [views]
    );

    const personal = useMemo(() => views.filter((v) => !v.assigned_to_portal), [views]);

    // Reportar la cantidad de vistas asignadas hacia arriba (resumen de la card del módulo).
    useEffect(() => {
        if (onCountChange) onCountChange(assigned.length);
    }, [assigned.length, onCountChange]);

    const handleAction = (action, view) => {
        switch (action) {
            case 'open': {
                const url = buildLoadViewUrl(view);
                if (url) window.location.href = url;
                break;
            }
            case 'duplicate':
                setDuplicateView(view);
                break;
            case 'rename':
                setRenameView(view);
                break;
            case 'assign':
                if (!canAssignToPortal) {
                    toast.info(t('saved_views.upgrade_hint'));
                    return;
                }
                PortalReportsApi.assignToPortal(view.id).then((r) => {
                    if (r && r.status !== 'ERROR') {
                        toast.success(t('saved_views.assigned'));
                        fetchViews();
                        // La primera asignación puede prender el módulo Reportes en el
                        // backend: el dueño de la card re-lee la config (QA H3).
                        if (onAssigned) onAssigned();
                    } else {
                        toast.error(t('saved_views.toggle_error'));
                    }
                });
                break;
            case 'unassign':
                if (!view.assignment_id) {
                    toast.error(t('saved_views.toggle_error'));
                    return;
                }
                PortalReportsApi.unassignFromPortal(view.assignment_id).then(() => {
                    toast.success(t('saved_views.unassigned'));
                    fetchViews();
                });
                break;
            case 'delete':
                setDeleteView(view);
                break;
            default:
                break;
        }
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = assigned.findIndex((v) => v.id === active.id);
        const newIndex = assigned.findIndex((v) => v.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        const reordered = arrayMove(assigned, oldIndex, newIndex);
        const updatedViews = views.map((v) => {
            const idx = reordered.findIndex((r) => r.id === v.id);
            if (idx >= 0) return { ...v, portal_order: idx };
            return v;
        });
        setViews(updatedViews);

        PortalReportsApi.reorderAssignments(
            clientId,
            reordered.map((r) => r.assignment_id).filter(Boolean)
        ).then((r) => {
            if (!r || r.status === 'ERROR') {
                toast.error(t('portal_reports.reorder_error'));
                fetchViews();
            }
        });
    };

    const handleDeleteConfirm = () => {
        if (!deleteView) return;
        PortalReportsApi.deleteView(deleteView.id).then(() => {
            toast.success(t('portal_reports.deleted'));
            setDeleteView(null);
            fetchViews();
        });
    };

    const renderContent = () => {
        if (loading) {
            return <div className="portal-reports-loading"><Spinner /></div>;
        }
        if (views.length === 0) {
            return (
                <div className="portal-reports-empty">
                    <div className="saved-view-modal-eyebrow">
                        {t('portal_reports.empty_eyebrow')}
                    </div>
                    <p>{t('portal_reports.empty_body')}</p>
                    <Link
                        to={`${explorerPath()}?client_id=${clientId}`}
                        className="btn -alt portal-reports-empty-cta"
                    >
                        {t('portal_reports.cta_go_explorer')}
                    </Link>
                </div>
            );
        }

        return (
            <>
                <div className="portal-reports-section">
                    <div className="portal-reports-section-title">
                        {t('saved_views.section_assigned')} · {assigned.length} / {PORTAL_SOFT_CAP}
                    </div>
                    {assigned.length === 0 ? (
                        <div className="portal-reports-empty-section portal-reports-empty-assigned">
                            <FaIcon iconName="arrow-down" />
                            <span>{t('portal_reports.empty_assigned_hint')}</span>
                        </div>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={assigned.map((v) => v.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {assigned.map((v) => (
                                    <ViewRow
                                        key={v.id}
                                        view={v}
                                        isDraggable
                                        t={t}
                                        onAction={handleAction}
                                        canAssignToPortal={canAssignToPortal}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    )}
                </div>

                <div className="portal-reports-section">
                    <div className="portal-reports-section-title -muted">
                        {t('saved_views.section_personal')} · {personal.length}
                    </div>
                    {personal.length === 0 ? (
                        <div className="portal-reports-empty-section">
                            {t('portal_reports.no_personal_yet')}
                        </div>
                    ) : (
                        personal.map((v) => (
                            <ViewRow
                                key={v.id}
                                view={v}
                                isDraggable={false}
                                t={t}
                                onAction={handleAction}
                                canAssignToPortal={canAssignToPortal}
                            />
                        ))
                    )}
                </div>
            </>
        );
    };

    return (
        <div className="portal-reports-section-wrap">
            {!gatingLoading && !canAssignToPortal && (
                <div className="portal-reports-subscription-banner">
                    <div className="portal-reports-subscription-text">
                        <span className="saved-view-modal-eyebrow">
                            {t('portal_reports.upgrade_eyebrow')}
                        </span>
                        <p className="portal-reports-subscription-hint">
                            {t('portal_reports.upgrade_hint')}
                        </p>
                    </div>
                    <a href="/agency/subscription" className="btn -alt">
                        <FaIcon iconName="arrow-up-right-from-square" /> {t('saved_views.upgrade_cta')}
                    </a>
                </div>
            )}

            <div className="portal-reports-header-row">
                <div className="portal-reports-header-text">
                    <p className="portal-reports-header-hint">
                        {t('portal_reports.header_hint')}
                    </p>
                </div>
                <div className="portal-reports-header-actions">
                    <Link
                        to={`${explorerPath()}?client_id=${clientId}`}
                        className="btn -alt portal-reports-create-btn"
                    >
                        <FaIcon iconName="plus" /> {t('portal_reports.cta_create_in_explorer')}
                    </Link>
                </div>
            </div>

            {renderContent()}

            <DuplicateViewModal
                isOpen={duplicateView !== null}
                toggle={() => setDuplicateView(null)}
                sourceView={duplicateView}
                currentClientId={clientId}
                onDuplicated={() => { setDuplicateView(null); fetchViews(); }}
            />

            <EditViewMetadataModal
                isOpen={renameView !== null}
                toggle={() => setRenameView(null)}
                view={renameView}
                onSaved={() => { setRenameView(null); fetchViews(); }}
            />

            {deleteView && (
                <YesNoModal
                    isOpen={!!deleteView}
                    title={t('portal_reports.delete_title')}
                    question={t('portal_reports.delete_confirm', { name: deleteView.name })}
                    onYesAnswer={handleDeleteConfirm}
                    onNoAnswer={() => setDeleteView(null)}
                />
            )}
        </div>
    );
}
